import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
});

export const auth = {
  signUp({ email, password, name }) {
    return new Promise((resolve, reject) => {
      const attrs = [new CognitoUserAttribute({ Name: 'email', Value: email })];
      if (name) attrs.push(new CognitoUserAttribute({ Name: 'name', Value: name }));
      userPool.signUp(email, password, attrs, [], (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  },

  confirm({ email, code }) {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.confirmRegistration(code, true, (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  },

  resendCode({ email }) {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.resendConfirmationCode((err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  },

  signIn({ email, password }) {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
        onSuccess: (session) => resolve({ session, user }),
        onFailure: reject,
      });
    });
  },

  forgotPassword({ email }) {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.forgotPassword({ onSuccess: resolve, onFailure: reject });
    });
  },

  resetPassword({ email, code, newPassword }) {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      user.confirmPassword(code, newPassword, { onSuccess: resolve, onFailure: reject });
    });
  },

  signOut() {
    const user = userPool.getCurrentUser();
    if (user) user.signOut();
  },

  /** Returns the current valid session (refreshes if needed) or null. */
  currentSession() {
    return new Promise((resolve) => {
      const user = userPool.getCurrentUser();
      if (!user) return resolve(null);
      user.getSession((err, session) => {
        if (err || !session?.isValid()) return resolve(null);
        resolve({ session, user });
      });
    });
  },

  async getAccessToken() {
    const c = await this.currentSession();
    return c?.session?.getAccessToken()?.getJwtToken() ?? null;
  },

  async getUserInfo() {
    const c = await this.currentSession();
    if (!c) return null;
    const payload = c.session.getIdToken().decodePayload();
    return {
      sub: payload.sub,
      email: payload.email,
      name: payload.name,
    };
  },
};
